import { useState } from "react"
import { motion } from "framer-motion"
import { Check } from "lucide-react"

const plans = [
    {
        name: "Free",
        price: 0,
        duration: "/ 7 วัน",
        description: "สำหรับทดลองระบบ",
        features: [
            "3 ผู้ดูแลระบบ",
            "3 ผู้สร้างคอร์ส",
            "40 ผู้เรียน",
            "5 คอร์สเรียน",
            "30 บทเรียน",
            "10 การสอบ",
        ],
        button: "เริ่มต้นใช้งานฟรี",
        highlight: false,
    },
    {
        name: "Starter",
        price: 990,
        duration: "/เดือน",
        description: "สำหรับทีมขนาดเล็ก",
        features: [
            "15 ผู้ดูแลระบบ",
            "20 ผู้สร้างคอร์ส",
            "100 ผู้เรียน",
            "30 คอร์สเรียน",
            "200 บทเรียน",
            "50 การสอบ",
        ],
        button: "เลือกแผน Starter",
        highlight: false,
    },
    {
        name: "Pro",
        price: "4,900",
        duration: "/ 6 เดือน",
        description: "สำหรับองค์กรเติบโต",
        features: [
            "30 ผู้ดูแลระบบ",
            "50 ผู้สร้างคอร์ส",
            "500 ผู้เรียน",
            "50 คอร์สเรียน",
            "500 บทเรียน",
            "100 การสอบ",
        ],
        button: "เลือกแผน Pro",
        highlight: true,
        recommended: true,
    },
    {
        name: "Enterprise",
        price: "ติดต่อเรา",
        duration: "/ สัญญา 1 ปี",
        description: "จัดเต็มทุกฟีเจอร์",
        features: [
            "ไม่จำกัดผู้ดูแลระบบ",
            "ไม่จำกัดผู้สร้างคอร์ส",
            "ไม่จำกัดผู้เรียน",
            "ไม่จำกัดคอร์สเรียน",
            "ไม่จำกัดบทเรียน",
            "ไม่จำกัดชุดข้อสอบ"
        ],
        button: "ติดต่อฝ่ายขาย",
        highlight: false,
    },
]

export default function PricingPage() {
    const [billing, setBilling] = useState("monthly")

    return (
        <div className="min-h-screen bg-gray-50 py-16 px-6">

            {/* Header */}
            <div className="text-center mb-12">
                <h1 className="text-4xl font-semibold text-gray-800">
                    เลือกแผนที่เหมาะกับ <span className="text-pink-500">คุณ</span>
                </h1>

                <p className="text-gray-500 mt-3">
                    ยกระดับการเรียนรู้ด้วย kuizy.ai แพลตฟอร์มบริหารการสอนอัจฉริยะ
                </p>

                <p className="text-gray-500">เลือกแพ็กเกจที่ตอบโจทย์ความต้องการขององค์กรคุณ</p>

                {/* Toggle */}
                <div className="mt-6 flex justify-center">
                    <div className="bg-gray-200 rounded-full p-1 flex">
                        <button
                            onClick={() => setBilling("monthly")}
                            className={`px-6 py-2 rounded-full text-sm transition ${billing === "monthly"
                                ? "bg-pink-500 text-white"
                                : "text-gray-600"
                                }`}
                        >
                            รายเดือน
                        </button>

                        <button
                            onClick={() => setBilling("yearly")}
                            className={`px-6 py-2 rounded-full text-sm transition ${billing === "yearly"
                                ? "bg-pink-500 text-white"
                                : "text-gray-600"
                                }`}
                        >
                            รายปี
                        </button>
                    </div>
                </div>
            </div>

            {/* Plans */}
            <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-6">
                {plans.map((plan, i) => (
                    <motion.div
                        key={i}
                        whileHover={{ y: -8, scale: 1.02 }}
                        className={`relative rounded-3xl p-8 shadow-md transition
                                ${plan.highlight
                                ? "bg-pink-500 text-white scale-105 shadow-xl"
                                : "bg-white"
                            }`}
                    >
                        {plan.recommended && (
                            <div className="absolute -mt-6 right-4 bg-white text-pink-500 text-xs font-semibold px-3 py-1 rounded-full shadow">
                                แนะนำ
                            </div>
                        )}

                        {/* Price */}
                        <div className="mb-6">

                            <div className="mt-3 text-3xl font-bold">
                                {plan.price}
                                <span
                                    className={`text-sm ml-1 ${plan.highlight ? "text-white/80" : "text-gray-500"
                                        }`}
                                >
                                    {plan.duration}
                                </span>
                            </div>
                            <h2 className="text-2xl font-bold mt-4">{plan.name}</h2>

                            <p
                                className={`text-sm mt-1 ${plan.highlight ? "text-white/80" : "text-gray-500"
                                    }`}
                            >
                                {plan.description}
                            </p>
                        </div>

                        {/* Features */}
                        <ul className="space-y-3 mb-6">
                            {plan.features.map((f, idx) => (
                                <li key={idx} className="flex items-center gap-2 text-sm">
                                    <div className="text-white bg-pink-100 rounded-full p-[2px]"><span className="text-pink-500"><Check size={16} /></span></div>
                                    {f}
                                </li>
                            ))}
                        </ul>

                        {/* Button */}
                        <button
                            className={`w-full py-3 rounded-xl font-medium transition
                                    ${plan.highlight
                                    ? "bg-white text-pink-500 hover:bg-gray-100"
                                    : "bg-pink-100 text-pink-400 hover:bg-pink-200"
                                }`}
                        >
                            {plan.button}
                        </button>
                    </motion.div>
                ))}
            </div>
            <p style={{ textAlign: "center", marginTop: 40, fontSize: 13, color: "#bbb" }}>
                ราคาทั้งหมดไม่รวม VAT 7% • ยกเลิกได้ทุกเมื่อ • รองรับการชำระเงินผ่านบัตรเครดิต / โอนเงิน
            </p>
        </div>
    )
}